import { useEffect, useRef } from 'react';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query';
import type { MemberPreferences, PageFilterDefaults } from '@catalyst-psa/shared';

import { api } from '../../api/client.js';

/** Same key the dashboards / quick-access features use for the member's preferences. */
const preferencesKey = ['preferences'] as const;

function usePreferencesQuery(): UseQueryResult<MemberPreferences> {
  return useQuery({
    queryKey: preferencesKey,
    queryFn: () => api.get<MemberPreferences>('/preferences'),
    staleTime: 60_000,
  });
}

export interface FilterDefaultsHandle {
  /** True once the saved preferences have loaded (apply has run, if there was anything). */
  isLoaded: boolean;
  /** True when this page has a saved default. */
  hasSaved: boolean;
  isSaving: boolean;
  /** Persist the given filters as this page's default. */
  save: (current: PageFilterDefaults) => void;
  /** Remove this page's saved default. */
  clear: () => void;
}

/**
 * Per-page saved filter defaults, persisted in the member's profile.
 *
 * `apply` is invoked AT MOST ONCE, when the saved default for `pageKey` first loads,
 * so the page can seed its filter state. Values come back as untyped JSON — use the
 * `savedString` / `savedStringArray` / `savedBoolean` helpers to read them safely.
 */
export function useFilterDefaults(
  pageKey: string,
  apply: (saved: PageFilterDefaults) => void,
): FilterDefaultsHandle {
  const queryClient = useQueryClient();
  const preferences = usePreferencesQuery();
  const appliedRef = useRef(false);
  const applyRef = useRef(apply);
  applyRef.current = apply;

  const isLoaded = preferences.isSuccess || preferences.isError;
  const saved = preferences.data?.filterDefaults?.[pageKey] ?? null;

  useEffect(() => {
    if (appliedRef.current || !isLoaded) return;
    appliedRef.current = true;
    if (saved) applyRef.current(saved);
  }, [isLoaded, saved]);

  const mutation = useMutation({
    mutationFn: (value: PageFilterDefaults | null) =>
      api.put<MemberPreferences>('/preferences', { filterDefaults: { [pageKey]: value } }),
    onSuccess: (data) => {
      queryClient.setQueryData(preferencesKey, data);
    },
  });

  return {
    isLoaded,
    hasSaved: saved !== null,
    isSaving: mutation.isPending,
    save: (current) => mutation.mutate(current),
    clear: () => mutation.mutate(null),
  };
}

export function savedString(saved: PageFilterDefaults, key: string): string | null {
  const value = saved[key];
  return typeof value === 'string' ? value : null;
}

export function savedStringArray(saved: PageFilterDefaults, key: string): string[] | null {
  const value = saved[key];
  if (!Array.isArray(value)) return null;
  return value.filter((v): v is string => typeof v === 'string');
}

export function savedBoolean(saved: PageFilterDefaults, key: string): boolean | null {
  const value = saved[key];
  return typeof value === 'boolean' ? value : null;
}
